import { CSSProperties, UIEventHandler } from 'react';
import { ScrollArea, CheckedState } from '@bst/react-ui';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';

import { CellFilterItem } from './CellFilterItem';
import { CellFilterLoading } from './CellFilterLoading';


type FilterItem<T = string> = {
  value: T;
  label: string;
  isChecked: boolean;
};

type Props<T = string> = {
  data: FilterItem<T>[];
  height?: number | string;
  isLoading?: boolean;
  handleScroll?: UIEventHandler<HTMLDivElement>;
  onCheckedChange: (checked: CheckedState, item: FilterItem<T>) => void;
};

/**
 * List component for CellFilter dropdown. Separated to have better control of data loading when using CellFilter
 */
export function CellFilterValueList<T>({
  data,
  height,
  isLoading,
  handleScroll,
  onCheckedChange
}: Props<T>) {
  const { t } = useTranslation()
  const selectAllLabel = t("cellFilterSelectAll")

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => document.querySelector(".scrollarea__viewport.filter-list"),
    estimateSize: () => 68,
    overscan: 8,
  });

  const noData = !isLoading && data
    .filter((item) => item.label !== selectAllLabel).length === 0

  const renderFilterItems = (
    <div
      style={{
        width: '100%',
        position: 'relative',
        height: virtualizer.getTotalSize()
      }}
    >
      {virtualizer.getVirtualItems().map((virtualItem) => {
        const item = data[virtualItem.index]
        const style = {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: virtualItem.size,
          transform: `translateY(${virtualItem.start}px)`
        } as CSSProperties
        if (isLoading) {
          return (
            <CellFilterLoading
              key={`${virtualItem.key}-loading-item`}
              style={style}
            />
          )
        }
        return (
          <CellFilterItem
            key={`${item.value}-${virtualItem.key}`}
            label={item.label}
            isChecked={item.isChecked}
            onCheckedChange={(checked) => onCheckedChange(checked, item)}
            style={style}
          />
        )
      })}
    </div>
  )

  return (
    <>
      {noData ? (
        <div className="cell-filter-no-results">{t('filter.no_results')}</div>
      ) : (
        <ScrollArea
          style={{ height: height || 300 }}
          onScroll={handleScroll}
          data-testid="scroll-area"
          className="filter-list"
        >
          {renderFilterItems}
        </ScrollArea >
      )}
    </>
  );
}